import {JSEncrypt} from "jsencrypt";

type GroupByProperty<T> = keyof T | string;

export function groupBy<T>(array: T[], property: GroupByProperty<T>){
    const hash: Record<string, T[]> = {};
    // split the properties into an array by '.'
    const props = property.toString().split(".");
    // loop through the array
    for (let i = 0; i < array.length; i++) {
      // get the current item
      const item = array[i];
      // get the current property value
      const key = props.reduce(function (acc, prop) {
        // if acc is object, get the property value if not, return acc
        return acc && acc[prop];
      }, item as any);
      // if the key is not in the hash, add it
      if (!hash[key]) hash[key] = [];
      // add the current item to the hash
      hash[key].push(item);
    }
    return hash;
  }




  export function encrypt(text: string, publicKeyPem: string): string | null {
    const encryptor = new JSEncrypt();
    encryptor.setPublicKey(publicKeyPem);

    const encryptedString = encryptor.encrypt(text);
    if (!encryptedString) {
        console.error("Error encrypting string");
        return null;
    }

    return encryptedString; // Base64 encode the encrypted string
}

/**
 * Acronyms that should stay fully uppercase when formatting role strings,
 * e.g. PARTNER_MSP_ADMIN -> "Partner MSP Admin". Add new ones here as needed.
 */
const ROLE_ACRONYMS = new Set(["AI", "MSP", "MSO", "RAN", "KPI"]);

/**
 *
 * @param role - The role string e.g. READONLY_TENANT or PARTNER_MSP_ADMIN
 * @returns The formatted role string e.g. Readonly Tenant or Partner MSP Admin
 */
export function roleStringFormatter(role: string) {
  return role
    .split("_")
    .filter(Boolean)
    .map(word => {
      if (ROLE_ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Converts meters to feet
 * @param meters - The value in meters to convert
 * @returns The equivalent value in feet
 */
export function metersToFeet(meters: number): number {
  return meters * 3.28084;
}


export const TimeFormat = "MMM DD, YYYY h:mm:ss a z";
export const ShortTimeFormat = "MMM DD, YYYY h:mm a";
export const DateTimeFormat = "ddd, MMM DD, YYYY hh:mm:ss.SSS a z";
export const GraphAxisDatetimeFormat = "h:mm a<br />MMM D";
export const GraphAxisDatetimeFormatWithYear = "h:mm a<br />MMM D, YYYY";

/**
 * Normalize epoch timestamps from APIs for display.
 * Values ≥ 1e12 are treated as milliseconds; smaller values as seconds (matches SummaryMetricsTable).
 */
export function toEpochMillisForDisplay(n: number): number {
  return n >= 1_000_000_000_000 ? n : n * 1000;
}

// --- JWT Helper Functions ---

/**
 * Decodes a base64 url encoded string
 * @param input - The base64 url encoded string
 * @returns The decoded string
 */
export function base64UrlDecode(input: string) {
    const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const atobFn = (globalThis as any)?.atob;
    if (typeof atobFn !== "function") {
        throw new Error("base64 decode not supported");
    }
    return atobFn(padded);
}

/**
 * Parses the payload of a JWT token
 * @param token - The JWT token
 * @returns The payload of the JWT token
 */
export function parseJwtPayload(token: string) {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
        const json = base64UrlDecode(parts[1]);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

/**
 * Checks if a JWT token is expired
 * @param token - The JWT token
 * @param clockSkewSeconds - The number of seconds to add to the expiration time
 * @returns True if the JWT token is expired, false otherwise
 */
export function isJwtExpired(token: string, clockSkewSeconds = 30) {
    const payload = parseJwtPayload(token);
    if (!payload || typeof payload.exp !== "number") return true; // invalid/missing exp treated as unusable
    const nowSeconds = Math.floor(Date.now() / 1000);
    return nowSeconds >= (payload.exp - clockSkewSeconds);
}